import "./modal.less";
import UIkit from "uikit";

export interface ModalChoice {
    value: string;
    text: string;
    primary?: boolean;
}

export interface ModalChooseDetail {
    label: string;
    text: string;
    highlight?: boolean;
}

export interface ModalChooseOptions {
    dialogClass?: string;
    details?: ModalChooseDetail[];
    footerMessage?: string;
    stack?: boolean;
}

export interface ModalInputOptions {
    title: string;
    label: string;
    message?: string;
    value?: string;
    placeholder?: string;
    type?: 'text' | 'number';
    min?: number;
    max?: number;
    step?: number;
    maxLength?: number;
    required?: boolean;
    confirmText?: string;
    cancelText?: string;
    helpText?: string;
    validate?: (value: string) => string | null;
    dialogClass?: string;
}

export default class Modal {

    static confirm(mensagem: string, ok: string = 'Confirmar', cancel: string = 'Cancelar'): Promise<boolean> {
        const promise = UIkit.modal.confirm(mensagem, {
            i18n: { ok: ok, cancel: cancel },
            stack: true
        } as any);

        // Centraliza verticalmente. UIkit nao tem opcao nativa em modal.confirm.
        // Aplicamos uma classe que muda o display do modal para flex (ver modal.less).
        // O modal e adicionado ao DOM sincronamente, mas .uk-open chega no proximo frame,
        // entao usamos requestAnimationFrame para garantir que a classe persista junto.
        requestAnimationFrame(() => {
            const todos = document.querySelectorAll<HTMLElement>('.uk-modal');
            const ultimo = todos[todos.length - 1];
            ultimo?.classList.add('uk-modal-vcenter');
        });

        return promise
            .then(() => true)   // Usuário confirmou
            .catch(() => false); // Usuário cancelou;
    }

    static choose(
        titulo: string,
        mensagem: string,
        opcoes: ModalChoice[],
        options: ModalChooseOptions = {}
    ): Promise<string | null> {
        return new Promise((resolve) => {
            const botoes = opcoes
                .map((opcao) =>
                    `<button type="button" class="uk-button ${opcao.primary ? "uk-button-primary" : "uk-button-default"}" data-modal-choice="${Modal.escape(opcao.value)}">${Modal.escape(opcao.text)}</button>`)
                .join(" ");
            const detalhes = (options.details || [])
                .map((detalhe) =>
                    `<div class="modal-choose-detail${detalhe.highlight ? " modal-choose-detail-highlight" : ""}">` +
                    `<strong class="modal-choose-detail-label">${Modal.escape(detalhe.label)}</strong>` +
                    `<p class="modal-choose-detail-text">${Modal.escape(detalhe.text)}</p>` +
                    `</div>`)
                .join("");
            const detalhesHtml = detalhes
                ? `<div class="modal-choose-details">${detalhes}</div>`
                : "";
            const rodapeMensagem = options.footerMessage
                ? `<p class="modal-choose-footer-message">${Modal.escape(options.footerMessage)}</p>`
                : "";
            const dialog: any = UIkit.modal.dialog(
                `<div class="uk-modal-header"><h2 class="uk-modal-title">${Modal.escape(titulo)}</h2></div>` +
                `<div class="uk-modal-body"><p class="modal-choose-message">${Modal.escape(mensagem)}</p>${detalhesHtml}${rodapeMensagem}</div>` +
                `<div class="uk-modal-footer uk-text-right">${botoes}</div>`,
                { bgClose: false, escClose: true, stack: options.stack ?? false }
            );
            let resolvido = false;

            dialog.$el.classList.add("uk-modal-vcenter");
            if (detalhes)
                dialog.$el.classList.add("modal-choose-with-details");
            if (options.dialogClass)
                dialog.$el.classList.add(options.dialogClass);

            const elementos = dialog.$el.querySelectorAll("[data-modal-choice]") as NodeListOf<HTMLElement>;
            elementos.forEach((botao) => {
                botao.addEventListener("click", () => {
                    resolvido = true;
                    resolve(botao.dataset.modalChoice || null);
                    dialog.hide();
                });
            });
            dialog.$el.addEventListener("hidden", () => {
                if (!resolvido)
                    resolve(null);
            }, { once: true });
        });
    }

    static input(options: ModalInputOptions): Promise<string | null> {
        return new Promise((resolve) => {
            const tipo = options.type ?? 'text';
            const atributos = [
                `type="${tipo}"`,
                'class="uk-input"',
                'data-modal-input',
                options.required === false ? '' : 'required',
                options.placeholder ? `placeholder="${Modal.escape(options.placeholder)}"` : '',
                options.min != null ? `min="${options.min}"` : '',
                options.max != null ? `max="${options.max}"` : '',
                options.step != null ? `step="${options.step}"` : '',
                options.maxLength != null ? `maxlength="${options.maxLength}"` : '',
                `value="${Modal.escape(options.value ?? '')}"`
            ].filter(Boolean).join(' ');
            const dialog: any = UIkit.modal.dialog(
                `<form class="uk-form-stacked" data-modal-input-form>` +
                `<div class="uk-modal-header"><h2 class="uk-modal-title">${Modal.escape(options.title)}</h2></div>` +
                `<div class="uk-modal-body">` +
                `${options.message ? `<p>${Modal.escape(options.message)}</p>` : ''}` +
                `<label class="uk-form-label">${Modal.escape(options.label)}</label>` +
                `<div class="uk-form-controls"><input ${atributos}></div>` +
                `${options.helpText ? `<p class="uk-text-meta uk-margin-small-top">${Modal.escape(options.helpText)}</p>` : ''}` +
                `<p class="uk-text-danger uk-margin-small-top uk-margin-remove-bottom" data-modal-input-error hidden></p>` +
                `</div>` +
                `<div class="uk-modal-footer uk-text-right">` +
                `<button type="button" class="uk-button uk-button-default" data-modal-input-cancel>${Modal.escape(options.cancelText ?? 'Cancelar')}</button> ` +
                `<button type="submit" class="uk-button uk-button-primary">${Modal.escape(options.confirmText ?? 'Confirmar')}</button>` +
                `</div></form>`,
                { bgClose: false, escClose: true, stack: true }
            );
            let resolvido = false;
            const raiz = dialog.$el as HTMLElement;
            const form = raiz.querySelector<HTMLFormElement>('[data-modal-input-form]')!;
            const input = raiz.querySelector<HTMLInputElement>('[data-modal-input]')!;
            const erro = raiz.querySelector<HTMLElement>('[data-modal-input-error]')!;

            raiz.classList.add('uk-modal-vcenter');
            if (options.dialogClass)
                raiz.classList.add(options.dialogClass);

            form.addEventListener('submit', (event) => {
                event.preventDefault();
                if (!input.reportValidity())
                    return;

                const valor = input.value.trim();
                const mensagemValidacao = options.validate?.(valor) ?? null;
                if (mensagemValidacao) {
                    erro.textContent = mensagemValidacao;
                    erro.hidden = false;
                    input.focus();
                    return;
                }

                resolvido = true;
                resolve(valor);
                dialog.hide();
            });
            raiz.querySelector<HTMLElement>('[data-modal-input-cancel]')
                ?.addEventListener('click', () => dialog.hide());
            raiz.addEventListener('hidden', () => {
                if (!resolvido)
                    resolve(null);
            }, { once: true });
            requestAnimationFrame(() => input.focus());
        });
    }

    private static escape(valor: string): string {
        return (valor || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
}
